import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionQaDetail } from './hooks/useConstructionQaDetail';
import { Button, Card, FeaturePlaceholder, Alert, KPICard } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { toast } from 'sonner';
import { QaStatusBadge } from './components/QaStatusBadge';
import { QaResultBadge } from './components/QaResultBadge';
import { QaInspectionOverview } from './components/QaInspectionOverview';

export default function QaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { inspection: item, isLoading, updateInspection } = useConstructionQaDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết phiếu kiểm tra...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin phiếu kiểm tra</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateInspection({ status_code: newStatus as any });
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'checklist', label: 'Nội dung kiểm tra' },
    { value: 'photos', label: 'Hình ảnh & Video' },
    { value: 'ncr', label: 'Báo cáo NCR' },
    { value: 'history', label: 'Lịch sử thay đổi' },
  ];

  return (
    <PageDetailLayout
      title={`Phiếu kiểm tra: ${item.inspection_code}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <QaStatusBadge status={item.status_code} />
          <QaResultBadge result={item.result_code} />
          <span className="text-sm text-slate-400 font-mono">#{item.id.substring(0, 8).toUpperCase()}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {item.inspected_at ? new Date(item.inspected_at).toLocaleDateString('vi-VN') : 'Chưa kiểm tra'}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Kiểm soát chất lượng', path: '/construction/qa' },
        { label: 'Chi tiết phiếu kiểm tra' }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'submitted' && (
            <>
              <AuthButton module="construction" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('passed')}>
                Phê duyệt & Đạt
              </AuthButton>
              <AuthButton module="construction" action="reject" variant="outline-danger" icon="ri-close-line" onClick={() => handleStatusChange('failed')}>
                Không đạt
              </AuthButton>
            </>
          )}
          <Button variant="outline" icon="ri-printer-line">In PDF</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Điểm đánh giá" value={item.score.toFixed(1)} icon="ri-star-line" variant="primary" />
                <KPICard title="Tỉ lệ đạt" value={item.pass_rate} icon="ri-percent-line" variant="success" suffix="%" />
                <KPICard title="NCR Liên quan" value={item.ncr_required ? 1 : 0} icon="ri-error-warning-line" variant={item.ncr_required ? 'danger' : 'neutral'} />
              </div>

              <QaInspectionOverview inspection={item} />

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-list-check text-slate-400" />
                  Đối tượng kiểm tra
                </h3>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-os-primary/10 flex items-center justify-center text-os-primary">
                      <i className="ri-layout-grid-line text-xl" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">{item.work_order?.title || '---'}</p>
                      <p className="text-[10px] text-slate-400">Mã WO: {item.work_order?.work_order_code || '---'}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" icon="ri-arrow-right-up-line">Xem WO</Button>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-slate-400" />
                  Trạng thái nghiệm thu
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                      <i className="ri-user-line" />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold ">Người lập phiếu</p>
                      <p className="text-xs font-bold text-slate-700">{item.inspector?.full_name}</p>
                    </div>
                  </div>

                  {item.approved_by && (
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-emerald-50 border border-emerald-100">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <i className="ri-check-double-line" />
                      </div>
                      <div>
                        <p className="text-[10px] text-emerald-600 font-bold ">Người phê duyệt</p>
                        <p className="text-xs font-bold text-slate-700">Nguyễn Văn Quản Lý</p>
                        <p className="text-[9px] text-slate-400">{item.approved_at ? new Date(item.approved_at).toLocaleString('vi-VN') : '---'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {item.ncr_required && (
                <Alert variant="danger" title="Cần lập báo cáo NCR" icon="ri-error-warning-fill">
                  Phiếu kiểm tra này không đạt và yêu cầu lập báo cáo không phù hợp (NCR) để theo dõi xử lý.
                  <Button variant="danger" size="sm" className="w-full mt-3" icon="ri-add-line">Lập NCR ngay</Button>
                </Alert>
              )}
            </div>
          </div>
        )}

        {(activeTab === 'checklist' || activeTab === 'photos' || activeTab === 'ncr' || activeTab === 'history') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu chi tiết về checklist nghiệm thu, tọa độ GPS ảnh chụp và tiến độ khắc phục NCR đang được đồng bộ."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
